import React, { FC, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { VersionsSelector } from '../../components/versions-selector/VersionsSelector';
import { Timeline } from '../../../../../../components/timeline/Timeline';
import { CompareVersionsModal } from '../../components/compare-versions-modal/CompareVersionsModal';
import { Column } from '../../../../../../components/grid/Grid';
import {
  SecondaryButton,
  TextButton,
} from '../../../../../../components/button';

import { useRestrictedModal } from '../../../../../../components/modal/useRestrictedModal';
import { useUser } from '../../../../../../user/useUser';
import { useSearchParamsModal } from '../../../../../../components/modal/useSearchParamsModal';

import { getFormattedLegislationsVersions } from '../../utils/utils';
import { doctrineAnalytics } from '../../../../../../utils/analyticsUtils';

import { LegislationInfos } from '../../../../legislation.types';

import styles from './versions.module.scss';

type Props = {
  legislationInfos: LegislationInfos;
  legislationInfosAll: LegislationInfos[];
  articleId: string;
  handleVersionChange: Function;
};

export const Versions: FC<Props> = ({
  legislationInfos,
  legislationInfosAll,
  articleId,
  handleVersionChange,
}) => {
  const intl = useIntl();
  const { accessPremium } = useUser();

  const formattedLegislationsVersions = getFormattedLegislationsVersions(
    legislationInfos,
    legislationInfosAll,
    intl,
  );

  const { openModal } = useSearchParamsModal(
    'compare-versions',
    ({ isOpen, onRequestClose }) => (
      <CompareVersionsModal
        isOpen={isOpen}
        onRequestClose={() => {
          doctrineAnalytics('Law CompareVersionClose', {
            articleId,
          });
          onRequestClose();
        }}
        legislationInfosAll={legislationInfosAll}
        articleId={articleId}
        articleTitle={legislationInfos.title}
        defaultContent={legislationInfos.rawContent}
      />
    ),
  );
  const { openRestrictedModal } = useRestrictedModal();

  const handleOnClickCompareVersions = () => {
    if (!accessPremium) {
      openRestrictedModal({ source: 'compare_versions' });
      return;
    }
    doctrineAnalytics('Law CompareVersionButtonClicked', {
      articleId,
    });
    openModal();
  };

  const renderVersionsView = useMemo(() => {
    if (!legislationInfos.startingDate) return null;
    if (legislationInfosAll.length >= 9) {
      return (
        <div className={styles.selectorContainer}>
          <VersionsSelector
            compareVersions={handleVersionChange}
            articleId={articleId}
            legislationInfosAll={legislationInfosAll}
          />
          <div className={styles.compareBtn}>
            <SecondaryButton
              onClick={handleOnClickCompareVersions}
              data-test-id="compare-versions-modal-btn"
            >
              <FormattedMessage id="sections/versions/Versions.tsx/1" />
            </SecondaryButton>
          </div>
        </div>
      );
    }
    return (
      <Timeline
        items={formattedLegislationsVersions}
        onItemClick={handleVersionChange}
      >
        {legislationInfosAll.length > 1 && (
          <div className={styles.btnTimelineContainer}>
            <TextButton
              onClick={handleOnClickCompareVersions}
              data-test-id="compare-versions-modal-btn"
              iconLeft="compare_arrows"
            >
              <FormattedMessage id="sections/versions/Versions.tsx/1" />
            </TextButton>
          </div>
        )}
      </Timeline>
    );
  }, [articleId, legislationInfosAll.length]);

  return <Column className={styles.container}>{renderVersionsView}</Column>;
};
